// University of Notre Dame: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Notre Dame's Common Data Set 2025-2026 (CDS_2025-2026.pdf), the file
//    Institutional Research links from its Common Data Set page (the "View
//    CDS reports" button opens a Google Drive folder; the SOURCES url is the
//    nd.edu page that links it). C1 applicants/admits/enrollees for Fall 2025
//    with the residency table; C8 test policy for Fall 2027; C21/C22 early
//    plans; G1/G5 2026-2027 costs.
//  - admissions.nd.edu: Restrictive Early Action and Regular Decision,
//    Application Overview, Standardized Testing Policy, Business Major Policy,
//    FAQs. Notre Dame stays test-optional for fall 2027 entry and requires the
//    SAT or ACT for fall 2028 entry. The deadline pages carry no year; they are
//    the pages as posted for the application that opened August 1, 2026, and
//    they say test-optional "through the 2026/2027 application cycle".
//  - College Scorecard, school id 152080 (net price by family income). The
//    DEMO_KEY API was over its rate limit on 2026-10-01, so the figures were
//    read from the Scorecard's own school page, which shows the same latest
//    year the API serves (checked against Princeton's API figures).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Notre Dame Common Data Set 2025-2026',
        url: 'https://iris.nd.edu/institutional-research/common-data-set-cds/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Notre Dame Admissions, Restrictive Early Action and Regular Decision',
        url: 'https://admissions.nd.edu/apply/early-action-regular-decision/',
        read: '2026-10-01',
    },
    overview: {
        label: 'Notre Dame Admissions, Application Overview',
        url: 'https://admissions.nd.edu/apply/application-overview/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Notre Dame Admissions, Standardized Testing Policy',
        url: 'https://admissions.nd.edu/apply/standardized-testing-policy/',
        read: '2026-10-01',
    },
    businessPolicy: {
        label: 'Notre Dame Admissions, Business Major Policy',
        url: 'https://admissions.nd.edu/apply/resources-for/business-major-policy/',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Notre Dame Admissions, FAQs',
        url: 'https://admissions.nd.edu/apply/faqs/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?152080-University-of-Notre-Dame',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-notre-dame',
    kbId: 'university_of_notre_dame',
    name: 'University of Notre Dame',
    shortName: 'Notre Dame',
    aliases: ['ND'],
    location: 'Notre Dame, IN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Notre Dame brand colors); decoration only.
    branding: {
        primary: '#0C2340',
        secondary: '#AE9142',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_notre_dame_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 35401,
        admitted: 3320,
        enrolled: 2118,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2069, admitted: 219 },
            { group: 'Out-of-state', applied: 26548, admitted: 2813 },
            { group: 'International', applied: 6784, admitted: 288 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Notre Dame\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Restrictive early action',
        note: 'Notre Dame has no early decision plan, and the Common Data Set does not report how many applied or were admitted in restrictive early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name the college and major you intend when you apply. Two areas work differently:',
        rows: [
            { school: 'Mendoza College of Business', requirement: 'If you say you intend to major in business, you learn at admission whether you are pre-approved to do so. Without pre-approval, the chance to move into business after the first year is extremely limited.', source: 'businessPolicy' },
            { school: 'Art, music and architecture', requirement: 'Optional: Notre Dame encourages supplementary material only from students interested in art, music or architecture, sent through SlideRoom after you submit the application.', source: 'faqs' },
        ],
        note: 'SlideRoom submissions are due November 15 for Restrictive Early Action and January 15 for Regular Decision.',
        source: 'overview',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Restrictive Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'Mid to late March', binding: false },
        ],
        note: 'Supporting documents are due November 15 for Restrictive Early Action and January 15 for Regular Decision. Admitted students confirm by May 1.',
        source: 'overview',
        binding: 'Restrictive Early Action is not binding: if admitted, you have until May 1 to decide. You may apply to other early action programs, public or private, but not to any college\'s binding Early Decision I program. You may apply to an Early Decision II program; if admitted there, withdraw your Notre Dame application. If you are denied in Restrictive Early Action, you cannot apply again in Regular Decision.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Notre Dame is test-optional for students applying to enroll in fall 2027. If you send SAT or ACT scores, they are considered, and Notre Dame superscores: you are evaluated on your highest score in each section across sittings. You may self-report scores on the application.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 28.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Notre Dame requires the SAT or ACT from first-year applicants seeking to enroll in fall 2028, starting with the 2027-2028 admissions cycle.', source: 'tests' },
        ],
        source: 'overview',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69280 },
            { item: 'Required fees', amount: 514 },
            { item: 'Food and housing (on campus)', amount: 18992 },
            { item: 'Books and supplies', amount: 1250 },
            { item: 'Transportation', amount: 750 },
            { item: 'Other expenses', amount: 1200 },
        ],
        total: 91986,
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 25339,
            byIncome: [
                { income: '$0 to $30,000', amount: 8380 },
                { income: '$30,001 to $48,000', amount: 7088 },
                { income: '$48,001 to $75,000', amount: 13838 },
                { income: '$75,001 to $110,000', amount: 18966 },
                { income: 'Over $110,000', amount: 45146 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Notre Dame\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
